// src/features/account/components/SubscriptionTab.jsx
import React, { useState, useEffect } from 'react';
import { Crown, Check, ExternalLink, AlertTriangle, XCircle } from 'lucide-react';
import { useWorkOS } from '../../workos/context/WorkOSContext';
import AddPaymentMethodModal from './AddPaymentMethodModal';

const PRO_MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID;
const PRO_ANNUAL_PRICE_ID = import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID;

const SubscriptionTab = () => {
  const { user, getValidAccessToken } = useWorkOS();

  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState(false);

  const tier = subscription?.tier || 'free';
  const isPro = tier === 'pro';

  useEffect(() => {
    fetchSubscription();
  }, [user]);

  const fetchSubscription = async () => {
    if (!user?.accessToken) return;

    try {
      setIsLoading(true);
      const accessToken = await getValidAccessToken();
      const response = await fetch('/.netlify/functions/get-subscription', {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setError(null);
    setSuccessMessage(null);

    try {
      // Always require a payment method on file before upgrading
      const accessToken = await getValidAccessToken();
      const pmResponse = await fetch('/.netlify/functions/check-payment-method', {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (pmResponse.ok) {
        const pmData = await pmResponse.json();
        if (!pmData.hasPaymentMethod) {
          setPendingUpgrade(true);
          setShowPaymentModal(true);
          return;
        }
      }

      await doUpgrade();
    } catch (err) {
      setError(err.message);
    }
  };

  const doUpgrade = async () => {
    setIsUpgrading(true);
    setError(null);

    try {
      const accessToken = await getValidAccessToken();
      const priceId = billingCycle === 'monthly' ? PRO_MONTHLY_PRICE_ID : PRO_ANNUAL_PRICE_ID;

      const response = await fetch('/.netlify/functions/upgrade-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ priceId, ...(promoCode.trim() && { promoCode: promoCode.trim() }) })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upgrade');
      }

      if (data.subscription?.status === 'active') {
        setSuccessMessage('Upgraded to Pro successfully!');
        setPromoCode('');
        fetchSubscription();
      } else if (data.subscription?.status === 'past_due' || data.clientSecret) {
        setError('Payment could not be processed. Please check your card details.');
      } else {
        setSuccessMessage('Upgrade initiated! Your Pro access will activate once payment is confirmed.');
        fetchSubscription();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpgrading(false);
    }
  };

  const handlePaymentMethodAdded = () => {
    setShowPaymentModal(false);
    if (pendingUpgrade) {
      setPendingUpgrade(false);
      doUpgrade();
    }
  };

  const handleCancelOrReactivate = async (action) => {
    setError(null);
    setSuccessMessage(null);
    setIsCancelling(true);
    setShowCancelConfirm(false);

    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch('/.netlify/functions/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ action })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to ${action} subscription`);
      }

      setSuccessMessage(
        action === 'cancel'
          ? 'Subscription cancelled. You\'ll retain Pro access until the end of your billing period.'
          : 'Subscription reactivated! Your Pro access will continue.'
      );
      fetchSubscription();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch('/.netlify/functions/stripe-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank');
      }
    } catch (err) {
      setError('Failed to open subscription management');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-800 rounded w-48 mb-4"></div>
          <div className="h-32 bg-gray-800 rounded mb-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Crown className="w-6 h-6 text-indigo-400" />
        <h2 className="text-xl font-bold text-white">Subscription</h2>
      </div>

      {/* Current plan card */}
      <div className={`rounded-lg p-6 border ${isPro ? 'border-indigo-500 bg-indigo-950/20' : 'border-gray-700 bg-gray-900'}`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {isPro ? 'Pro' : 'Free'} Plan
            </h3>
            {isPro && subscription?.plan && (
              <p className="text-gray-400 text-sm">
                ${(subscription.plan.amount / 100).toFixed(0)}/{subscription.plan.interval === 'year' ? 'year' : 'month'}
              </p>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
            isPro ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300'
          }`}>
            {isPro ? 'PRO' : 'FREE'}
          </span>
        </div>

        {isPro && subscription?.currentPeriodEnd && (
          <p className="text-gray-400 text-sm">
            {subscription.cancelAtPeriodEnd
              ? `Cancels on ${new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}`
              : `Renews on ${new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}`
            }
          </p>
        )}

        {isPro && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Your Pro benefits:</h4>
            <ul className="space-y-2">
              {['All official workflows free', 'Private workflow uploads (1GB)', 'Sync workflows across all your devices'].map(f => (
                <li key={f} className="flex items-center text-gray-400 text-sm">
                  <Check className="w-4 h-4 text-indigo-400 mr-2 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Cancel/Reactivate buttons for Pro users */}
        {isPro && (
          <div className="mt-6 pt-4 border-t border-gray-700">
            {subscription?.cancelAtPeriodEnd ? (
              <button
                onClick={() => handleCancelOrReactivate('reactivate')}
                disabled={isCancelling}
                className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg
                         transition-colors disabled:opacity-50"
              >
                {isCancelling ? 'Processing...' : 'Reactivate Subscription'}
              </button>
            ) : showCancelConfirm ? (
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-sm">Are you sure?</span>
                <button
                  onClick={() => handleCancelOrReactivate('cancel')}
                  disabled={isCancelling}
                  className="text-sm bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg
                           transition-colors disabled:opacity-50"
                >
                  {isCancelling ? 'Cancelling...' : 'Yes, cancel'}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="text-sm text-gray-400 hover:text-white px-3 py-2 transition-colors"
                >
                  No, keep it
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="text-sm text-gray-500 hover:text-red-400 transition-colors"
              >
                Cancel subscription
              </button>
            )}
          </div>
        )}

        {/* Cancellation warning */}
        {isPro && subscription?.cancelAtPeriodEnd && (
          <div className="mt-4 flex items-start gap-3 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-amber-300 font-medium">Your subscription is ending</p>
              <p className="text-amber-400/80 mt-1">
                When your subscription expires on {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}, your private workflows will become inaccessible until you re-subscribe. Please download any private workflows you need before this date. Public workflows are not affected.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error / Success */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm flex items-start gap-2">
          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {successMessage && (
        <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-3 text-emerald-300 text-sm">
          {successMessage}
        </div>
      )}

      {/* Upgrade CTA for free users */}
      {!isPro && (
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Upgrade to Pro</h3>

          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                billingCycle === 'monthly' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              $10/month
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                billingCycle === 'annual' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              $100/year <span className="text-emerald-400 text-xs">Save $20</span>
            </button>
          </div>

          <ul className="space-y-2 mb-6">
            {['All official workflows free', 'Private workflow uploads (1GB storage)', 'Sync workflows across all your devices'].map(f => (
              <li key={f} className="flex items-center text-gray-400 text-sm">
                <Check className="w-4 h-4 text-indigo-400 mr-2 flex-shrink-0" />
                {f}
              </li>
            ))}
          </ul>

          <div className="mb-4">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              placeholder="Promo code (optional)"
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm
                       placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            />
          </div>

          <button
            onClick={handleUpgrade}
            disabled={isUpgrading}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg
                     hover:from-blue-700 hover:to-indigo-700 transition-colors disabled:opacity-50 font-semibold"
          >
            {isUpgrading ? 'Upgrading...' : `Upgrade to Pro - $${billingCycle === 'monthly' ? '10/mo' : '100/yr'}`}
          </button>
        </div>
      )}

      {/* Manage on Stripe link */}
      <button
        onClick={handleManageSubscription}
        className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
      >
        <ExternalLink className="w-4 h-4" />
        Manage on Stripe
      </button>

      {/* Payment method modal */}
      <AddPaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPendingUpgrade(false); }}
        onSuccess={handlePaymentMethodAdded}
        context="upgrade"
      />
    </div>
  );
};

export default SubscriptionTab;
